<template>
    <div>
        <app-layout>
            <template v-slot:header>
                <Header :title="$t('coop_advisor_appointments')"
                        :isFilter="true"
                        :isNewButton="checkPermission('coopadvisorunavailability_store')"
                        :newButtonText="$t('coop_advisor_add_unavailability')"
                        @new-button-click="openUnavailabilityModal"
                        @filter-div-status="datatable.filterStatus = $event" />
            </template>
            <template v-slot:header-mobile>
                <HeaderMobile :title="$t('coop_advisor_appointments')"
                              :isFilter="true"
                              :isNewButton="checkPermission('coopadvisorunavailability_store')"
                              :newButtonText="$t('coop_advisor_add_unavailability')"
                              @new-button-click="openUnavailabilityModal"
                              @filter-div-status="datatable.filterStatus = $event" />
            </template>

            <datatable-filter v-show="datatable.filterStatus"
                               @filter="filter"
                               @filterClear="filterClear"
                               :export-excel="checkPermission('coopadvisorappointment_excelexport')"
                               @exportExcel="exportExcel">
                <b-row>
                    <b-col sm="6" md="4">
                        <b-form-group :label="$t('status')">
                            <parameter-selectbox code="coop_advisor_appointment.statuses" v-model="datatable.queryParams.filter.status" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="4">
                        <b-form-group :label="$t('student_number')">
                            <b-form-input v-model="datatable.queryParams.filter.student_number" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="4">
                        <b-form-group :label="$t('faculty')">
                            <faculty-selectbox v-model="datatable.queryParams.filter.faculty_code" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="4">
                        <b-form-group :label="$t('department')">
                            <department-selectbox v-model="datatable.queryParams.filter.department_code" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="4">
                        <b-form-group :label="$t('name')">
                            <b-form-input v-model="datatable.queryParams.filter.name" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="4">
                        <b-form-group :label="$t('surname')">
                            <b-form-input v-model="datatable.queryParams.filter.surname" />
                        </b-form-group>
                    </b-col>
                </b-row>
            </datatable-filter>

            <datatable ref="datatable"
                       :isLoading.sync="datatable.isLoading"
                       :columns="datatable.columns"
                       :rows="datatable.rows"
                       :total="datatable.total"
                       :queryParams="datatable.queryParams"
                       :lineNumbers="false"
                       @on-page-change="onPageChange"
                       @on-sort-change="onSortChange"
                       @on-per-page-change="onPerPageChange"
                       v-show="datatable.showTable"
            />

            <!-- Status Update Modal -->
            <CommonModal ref="statusModal" size="md" :onHideOnlyX="true">
                <template v-slot:CommonModalTitle>
                    {{ $t('status_update') }}
                </template>
                <template v-slot:CommonModalContent>
                    <b-form-group :label="$t('status')">
                        <parameter-selectbox code="coop_advisor_appointment.statuses" v-model="selectedStatus" />
                    </b-form-group>
                    <b-form-group :label="$t('reject_reason')" v-if="selectedStatus === 'rejected'">
                        <b-form-textarea v-model="rejectReason" rows="3" required />
                    </b-form-group>
                    <div class="d-flex justify-content-center mt-3">
                        <b-button variant="primary" @click="saveStatus">{{ $t('save') }}</b-button>
                    </div>
                </template>
            </CommonModal>

            <!-- Weekly Calendar Modal for Unavailability Management -->
            <CommonModal ref="unavailabilityModal" size="xl" :onHideOnlyX="true">
                <template v-slot:CommonModalTitle>
                    {{ $t('coop_advisor_add_unavailability') }}
                </template>
                <template v-slot:CommonModalContent>
                    <div class="p-2">
                        <div class="calendar-container p-4 rounded mb-4" style="background-color: #f7f3ed; border: 1px solid #e6dfd5;">
                            <div class="d-flex justify-content-between align-items-center mb-4">
                                <!-- Week Navigation -->
                                <div class="d-flex align-items-center">
                                    <b-button variant="light"
                                              size="sm"
                                              class="rounded-circle shadow-sm border mr-3"
                                              style="width: 32px; height: 32px; padding: 0;"
                                              @click="navigateWeek(-1)">
                                        <i class="ri-arrow-left-s-line" style="font-size: 1.2rem; line-height: 1;"></i>
                                    </b-button>
                                    <span class="font-weight-bold text-secondary" style="font-size: 1rem;">{{ getWeekRangeLabel() }}</span>
                                    <b-button variant="light"
                                              size="sm"
                                              class="rounded-circle shadow-sm border ml-3"
                                              style="width: 32px; height: 32px; padding: 0;"
                                              @click="navigateWeek(1)">
                                        <i class="ri-arrow-right-s-line" style="font-size: 1.2rem; line-height: 1;"></i>
                                    </b-button>
                                </div>
                            </div>

                            <!-- Calendar Grid -->
                            <b-row class="text-center mx-0 bg-white p-3 rounded shadow-sm">
                                <b-col v-for="day in weekDays"
                                       :key="day.dateStr"
                                       class="px-1 py-2 border-right last-no-border"
                                       style="min-width: 110px;">
                                    <div class="font-weight-bold text-dark mb-1" style="font-size: 0.9rem;">
                                        {{ day.dayName }}
                                    </div>
                                    <div class="text-muted mb-3" style="font-size: 0.8rem;">
                                        {{ day.formattedDate }}
                                    </div>

                                    <div>
                                        <b-button v-for="slot in day.slots"
                                                  :key="slot.time"
                                                  size="sm"
                                                  block
                                                  :variant="getSlotVariant(slot)"
                                                  class="mb-2 py-1 font-weight-bold slot-btn"
                                                  style="font-size: 0.8rem;"
                                                  @click="markSlotUnavailable(day, slot)">
                                            {{ slot.time }}
                                        </b-button>
                                    </div>
                                </b-col>
                            </b-row>
                        </div>
                    </div>
                </template>
            </CommonModal>
        </app-layout>
    </div>
</template>

<script>
    //Template
    import AppLayout from '@/layouts/AppLayout';
    import Header from '@/layouts/AppLayout/Header';
    import HeaderMobile from '@/layouts/AppLayout/HeaderMobile';

    //Component
    import DatatableFilter from '@/components/datatable/DatatableFilter';
    import Datatable from '@/components/datatable/Datatable';
    import CommonModal from '@/components/elements/CommonModal';
    import FacultySelectbox from '@/components/interactive-fields/FacultySelectbox';
    import DepartmentSelectbox from '@/components/interactive-fields/DepartmentSelectbox';
    import ParameterSelectbox from '@/components/interactive-fields/ParameterSelectbox';

    //Service
    import CoopAdvisorAppointmentService from '@/services/CoopAdvisorAppointmentService';

    //Other
    import moment from 'moment';
    import qs from 'qs';

    const generateSlotTimes = (startHour = 9, endHour = 17, intervalMinutes = 15) => {
        const slots = [];
        let current = moment().hours(startHour).minutes(0).seconds(0);
        const end = moment().hours(endHour).minutes(0).seconds(0);

        while (current.isSameOrBefore(end)) {
            slots.push(current.format('HH:mm'));
            current.add(intervalMinutes, 'minutes');
        }
        return slots;
    };

    const defaultSlotTimes = generateSlotTimes(9, 17, 15);

    export default {
        name: 'AdvisorAppointmentsIndex',
        components: {
            AppLayout,
            Header,
            HeaderMobile,
            DatatableFilter,
            Datatable,
            CommonModal,
            FacultySelectbox,
            DepartmentSelectbox,
            ParameterSelectbox
        },
        metaInfo() {
            return {
                title: this.$t('coop_advisor_appointments')
            };
        },
        data() {
            return {
                currentWeekOffset: 0,
                weekDays: [],
                selectedRow: null,
                selectedStatus: null,
                rejectReason: '',
                datatable: {
                    isLoading: false,
                    filterStatus: true,
                    columns: [
                        {
                            label: '',
                            field: 'buttons',
                            html: true,
                            hidden: false,
                            tdClass: 'p-0 text-center w-40 align-middle',
                            buttons: [
                                {
                                    text: this.$t('status_update'),
                                    class: 'ri-edit-line text-primary',
                                    permission: 'coopadvisorappointment_updatestatus',
                                    callback: (row) => {
                                        this.showStatusModal(row);
                                    }
                                },
                                {
                                    text: this.$t('delete'),
                                    class: 'ri-delete-bin-line text-danger',
                                    permission: 'coopadvisorappointment_delete',
                                    callback: (row) => {
                                        this.deleteAppointment(row);
                                    }
                                }
                            ]
                        },
                        {
                            label: this.toUpperCase('student_number'),
                            field: 'student_number',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('name'),
                            field: 'name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('surname'),
                            field: 'surname',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('department'),
                            field: 'department_name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('date'),
                            field: 'appointment_date',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_event_time'),
                            field: 'appointment_time',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('status'),
                            field: this.getLocaleField('status_name'),
                            hidden: false
                        }
                    ],
                    rows: [],
                    total: 0,
                    showTable: false,
                    queryParams: {
                        filter: {},
                        sort: 'id',
                        page: 1,
                        limit: 20
                    }
                }
            };
        },
        created() {
            this.getRows();
        },
        methods: {
            openUnavailabilityModal() {
                this.currentWeekOffset = 0;
                this.loadWeekSlots();
                this.$refs.unavailabilityModal.$refs.commonModal.show();
            },
            navigateWeek(offset) {
                this.currentWeekOffset += offset;
                this.loadWeekSlots();
            },
            getWeekRangeLabel() {
                const startOfWeek = moment().add(this.currentWeekOffset, 'weeks').startOf('isoWeek');
                const endOfWeek = moment().add(this.currentWeekOffset, 'weeks').endOf('isoWeek');
                return `${startOfWeek.format('DD.MM.YYYY')} - ${endOfWeek.format('DD.MM.YYYY')}`;
            },
            loadWeekSlots() {
                const startOfWeek = moment().add(this.currentWeekOffset, 'weeks').startOf('isoWeek');
                const endOfWeek = moment().add(this.currentWeekOffset, 'weeks').endOf('isoWeek');
                const startDateStr = startOfWeek.format('YYYY-MM-DD');
                const endDateStr = endOfWeek.format('YYYY-MM-DD');

                const config = {
                    params: {
                        start_date: startDateStr,
                        end_date: endDateStr
                    }
                };

                CoopAdvisorAppointmentService.getAvailableSlots(config)
                    .then((response) => {
                        const slotsData = response.data?.data || response.data || [];
                        const days = [];
                        for (let i = 0; i < 7; i++) {
                            const date = moment().add(this.currentWeekOffset, 'weeks').startOf('isoWeek').add(i, 'days');
                            const dateStr = date.format('YYYY-MM-DD');
                            const foundDay = Array.isArray(slotsData) ? slotsData.find(d => d.date === dateStr) : null;
                            const rawSlots = foundDay?.slots || defaultSlotTimes;

                            const slots = rawSlots.map(s => {
                                if (typeof s === 'string') {
                                    return { time: s, isUnavailable: false, id: null };
                                }
                                return {
                                    time: (s.start_time || s.time || '').substring(0, 5),
                                    isUnavailable: s.available === false || s.is_unavailable === true || s.status === 'unavailable',
                                    id: s.id || null
                                };
                            });

                            days.push({
                                dateStr: dateStr,
                                dayName: date.locale(this.$i18n.locale).format('dddd'),
                                formattedDate: date.format('DD.MM.YYYY'),
                                slots: slots
                            });
                        }
                        this.weekDays = days;

                        return CoopAdvisorAppointmentService.getUnavailabilities();
                    })
                    .then((response) => {
                        if (!response) return;
                        const items = response.data?.data || response.data || [];
                        items.forEach((item) => {
                            const itemDate = item.date ? moment(item.date.substring(0, 10)).format('YYYY-MM-DD') : (item.appointment_date ? moment(item.appointment_date.substring(0, 10)).format('YYYY-MM-DD') : null);
                            let itemTime = item.time ? item.time.substring(0, 5) : (item.appointment_time ? item.appointment_time.substring(0, 5) : null);
                            if (!itemTime && item.date && item.date.length > 10) {
                                itemTime = item.date.substring(11, 16);
                            }

                            const dayObj = this.weekDays.find((d) => d.dateStr === itemDate);
                            if (dayObj) {
                                const slotObj = dayObj.slots.find((s) => s.time === itemTime);
                                if (slotObj) {
                                    slotObj.isUnavailable = true;
                                    slotObj.id = item.id;
                                }
                            }
                        });
                    })
                    .catch((error) => {
                        this.showErrors(error, null);
                    });
            },
            getSlotVariant(slot) {
                if (slot.isUnavailable) {
                    return 'secondary';
                }
                return 'outline-success';
            },
            markSlotUnavailable(day, slot) {
                if (slot.isUnavailable) {
                    this.$swal.fire({
                        text: this.$t('coop_advisor_unavailability_delete_confirm'),
                        showCancelButton: true,
                        confirmButtonText: this.$t('yes'),
                        cancelButtonText: this.$t('no')
                    }).then((result) => {
                        if (result.isConfirmed) {
                            const removePromise = slot.id
                                ? CoopAdvisorAppointmentService.removeUnavailability(slot.id)
                                : Promise.resolve();

                            removePromise
                                .then((response) => {
                                    if (response && response.data && response.data.message) {
                                        this.$toast.success(this.$t('api.' + response.data.message));
                                    }
                                    slot.isUnavailable = false;
                                    slot.id = null;
                                    this.loadWeekSlots();
                                })
                                .catch((error) => {
                                    this.showErrors(error, null);
                                });
                        }
                    });
                } else {
                    this.$swal.fire({
                        text: this.$t('coop_mark_unavailable_confirm'),
                        showCancelButton: true,
                        confirmButtonText: this.$t('yes'),
                        cancelButtonText: this.$t('no')
                    }).then((result) => {
                        if (result.isConfirmed) {
                            CoopAdvisorAppointmentService.storeUnavailability({
                                date: day.dateStr,
                                time: slot.time
                            })
                                .then((response) => {
                                    this.$toast.success(this.$t('api.' + response.data.message));
                                    slot.isUnavailable = true;
                                    slot.id = response.data?.data?.id || response.data?.id || null;
                                })
                                .catch((error) => {
                                    this.showErrors(error, null);
                                });
                        }
                    });
                }
            },
            getRows() {
                if (!this.checkPermission('coopadvisorappointment_index')) {
                    return;
                }
                this.datatable.isLoading = true;
                const config = {
                    params: { ...this.datatable.queryParams },
                    paramsSerializer: (params) => qs.stringify(params, { encode: false })
                };

                CoopAdvisorAppointmentService.getAll(config)
                    .then((response) => {
                        this.datatable.rows = response.data.data;
                        this.datatable.total = response.data.pagination?.total || response.data.data.length;
                        this.datatable.showTable = true;
                    })
                    .catch((error) => {
                        this.showErrors(error, null);
                    })
                    .finally(() => {
                        this.datatable.isLoading = false;
                    });
            },
            exportExcel() {
                const config = {
                    params: { ...this.datatable.queryParams },
                    paramsSerializer: (params) => qs.stringify(params, { encode: false })
                };

                return CoopAdvisorAppointmentService.excelExport(config)
                    .then((response) => {
                        this._downloadFile(response, 'advisor_appointments.xlsx');
                    })
                    .catch((e) => {
                        this.showErrors(e, null);
                    });
            },
            filter() {
                this.datatable.queryParams.page = 1;
                this.getRows();
            },
            filterClear() {
                this.datatable.queryParams.filter = {};
                this.filter();
            },
            onPageChange(params) {
                this.datatable.queryParams.page = params.currentPage;
                this.getRows();
            },
            onPerPageChange(params) {
                this.datatable.queryParams.limit = params.currentPerPage;
                this.getRows();
            },
            onSortChange(params) {
                if (params && params.length > 0) {
                    this.datatable.queryParams.sort = (params[0].type === 'desc' ? '-' : '') + params[0].field;
                }
                this.getRows();
            },
            showStatusModal(row) {
                this.selectedRow = row;
                this.selectedStatus = row.status;
                this.rejectReason = row.reason || row.reject_reason || '';
                this.$refs.statusModal.$refs.commonModal.show();
            },
            saveStatus() {
                if (!this.selectedRow || !this.selectedStatus) return;
                const payload = {
                    status: this.selectedStatus
                };
                if (this.selectedStatus === 'rejected') {
                    payload.reason = this.rejectReason;
                }
                CoopAdvisorAppointmentService.updateStatus(this.selectedRow.id, payload)
                    .then((response) => {
                        this.$toast.success(this.$t('api.' + response.data.message));
                        this.$refs.statusModal.$refs.commonModal.hide();
                        this.getRows();
                    })
                    .catch((error) => {
                        this.showErrors(error, null);
                    });
            },
            deleteAppointment(row) {
                this.$swal.fire({
                    text: this.$t('coop_advisor_appointment_delete_confirm'),
                    showCancelButton: true,
                    confirmButtonText: this.$t('yes'),
                    cancelButtonText: this.$t('no')
                }).then((result) => {
                    if (result.isConfirmed) {
                        CoopAdvisorAppointmentService.remove(row.id)
                            .then((response) => {
                                this.$toast.success(this.$t('api.' + response.data.message));
                                this.getRows();
                            })
                            .catch((error) => {
                                this.showErrors(error, null);
                            });
                    }
                });
            }
        }
    };
</script>

<style scoped>
    .last-no-border:last-child {
        border-right: none !important;
    }
</style>
